// === ALNAP START === Demo Portal de Cotizaciones Corporativas
// ----------------------------------------------------------------------------
// Módulo provisional para ALNAP. Solo se renderiza si:
//   process.env.REACT_APP_ENABLE_ALNAP === "true"
// y el usuario tiene rol admin/administrador/superadmin.
//
// Para apagar: REACT_APP_ENABLE_ALNAP=false en frontend/.env
// Para borrar: eliminar este archivo + la ruta y el botón del Sidebar
// (todo lo marcado con // === ALNAP START === / // === ALNAP END ===).
// ----------------------------------------------------------------------------
import { useState, useRef, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Building2, Car, Users, FileText, Mail, CheckCircle2, ShieldCheck, Eye,
  Download, ArrowLeft, ArrowRight, Sparkles, Clock, MousePointerClick,
  Signature, FileSignature, Wand2, ChevronRight, Loader2, FileDown,
  Activity, TrendingUp, MailCheck, Send, BarChart3, FileCheck2, Lock,
  Share2, Copy, Trash2, Plus, Link2, AlertCircle, Settings,
  Pencil, Percent, ShieldAlert, Database,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 0, key: "intro", title: "Bienvenida", icon: Sparkles },
  { id: 1, key: "dashboard", title: "Dashboard ALNAP", icon: BarChart3 },
  { id: 2, key: "padron", title: "Padrón de Vehículos", icon: Car },
  { id: 3, key: "panel_qa", title: "Panel QAsesores", icon: Settings },
  { id: 4, key: "generador", title: "Generar Cotización", icon: Wand2 },
  { id: 5, key: "email", title: "Email al Empleado", icon: Mail },
  { id: 6, key: "portal", title: "Portal del Empleado", icon: Eye },
  { id: 7, key: "firma", title: "Firma Digital + OTP", icon: Signature },
  { id: 8, key: "constancia", title: "Constancia de Aprobación", icon: FileCheck2 },
];

// Datos demo
const DEMO_VEHICULOS = [
  { id: "V001", empleado: "María Fernández Castillo", cedula: "001-1234567-8", email: "mfernandez@alnap.com.do", marca: "Toyota", modelo: "Corolla", anio: 2023, chasis: "JTDB...8451", valor: 1450000, tipo: "Empleado", estado: "Activo" },
  { id: "V002", empleado: "Carlos Martínez Reyes", cedula: "001-9876543-2", email: "cmartinez@alnap.com.do", marca: "Honda", modelo: "Civic", anio: 2022, chasis: "1HGEM...3322", valor: 1280000, tipo: "Empleado", estado: "Activo" },
  { id: "V003", empleado: "Flotilla Corporativa #12", cedula: "RNC-101234567", email: "flotillas@alnap.com.do", marca: "Hyundai", modelo: "Tucson", anio: 2024, chasis: "KMHJ...7711", valor: 1850000, tipo: "Flotilla", estado: "Activo" },
  { id: "V004", empleado: "Ana Lucía Peguero", cedula: "402-5678901-3", email: "apeguero@alnap.com.do", marca: "Kia", modelo: "Sportage", anio: 2023, chasis: "KNDP...4499", valor: 1620000, tipo: "Empleado", estado: "Activo" },
  { id: "V005", empleado: "Rafael Antonio Sánchez", cedula: "001-3456789-0", email: "rsanchez@alnap.com.do", marca: "Mitsubishi", modelo: "Outlander", anio: 2024, chasis: "JA4AZ...2255", valor: 1980000, tipo: "Empleado", estado: "Activo" },
];

const DEMO_ASEGURADORAS = [
  { id: "mapfre", nombre: "Mapfre BHD Seguros", color: "from-rose-500 to-red-600", tasa: 3.2, deducible: 25000, primaAnual: 46400, coberturas: ["Cobertura amplia", "Asistencia 24/7", "Auto sustituto", "Daños materiales", "Robo total"], destacado: false },
  { id: "colonial", nombre: "Seguros La Colonial", color: "from-blue-500 to-indigo-600", tasa: 3.0, deducible: 30000, primaAnual: 43500, coberturas: ["Cobertura amplia", "Asistencia vial", "Cristales", "Daños materiales", "Robo total"], destacado: true },
  { id: "pepin", nombre: "Seguros Pepín", color: "from-amber-500 to-orange-600", tasa: 3.5, deducible: 20000, primaAnual: 50750, coberturas: ["Cobertura amplia", "Auto sustituto 7 días", "Asistencia vial", "Daños materiales", "Robo total"], destacado: false },
];

export default function AlnapDemoPage({ publicToken = null, publicMeta = null }) {
  const auth = useAuth();
  const user = auth?.user;
  const getAuthHeaders = auth?.getAuthHeaders;
  const isPublic = Boolean(publicToken);
  const [step, setStep] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [selectedAseguradora, setSelectedAseguradora] = useState(null);
  const [otp, setOtp] = useState("");
  const [signed, setSigned] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const canvasRef = useRef(null);

  // Verificar acceso (solo en modo admin — modo público se valida en el wrapper)
  const isAdmin = ["admin", "administrador", "superadmin"].includes((user?.role || "").toLowerCase());
  if (!isPublic && !isAdmin) {
    return (
      <div className="p-8 text-center text-slate-500">
        <Lock className="w-12 h-12 mx-auto mb-3 text-slate-400" />
        Acceso restringido a administradores.
      </div>
    );
  }

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));
  const goTo = (i) => setStep(i);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      // Modo público usa endpoint público con token; modo admin usa endpoint autenticado
      const url = isPublic
        ? `${API_URL}/api/alnap/public/${publicToken}/propuesta.pdf`
        : `${API_URL}/api/alnap/propuesta.pdf`;
      const opts = isPublic ? {} : (getAuthHeaders ? getAuthHeaders() : {});
      const r = await fetch(url, opts);
      if (!r.ok) throw new Error("PDF no disponible");
      const blob = await r.blob();
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = `Propuesta_PCC_ALNAP_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dlUrl);
      toast.success("Propuesta descargada");
    } catch {
      toast.error("Error al descargar el PDF ejecutivo");
    }
    setDownloading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-emerald-50/20" data-testid="alnap-demo-page">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#193966] to-[#0F2240] text-white shadow-xl">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
              <Building2 className="w-6 h-6 text-emerald-300" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-emerald-300 font-semibold">
                {isPublic ? "DEMO COMPARTIDO POR QASESORES" : "DEMO PROVISIONAL · ALNAP"}
              </p>
              <h1 className="text-xl font-bold leading-tight">Portal de Cotizaciones Corporativas</h1>
              <p className="text-xs text-blue-100/80 mt-0.5">
                {isPublic && publicMeta?.label
                  ? `${publicMeta.label} · Asociación La Nacional de Ahorros y Préstamos`
                  : "Asociación La Nacional de Ahorros y Préstamos"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isPublic ? (
              <Badge className="bg-blue-400/90 text-blue-950 border-blue-300 font-bold">
                <Eye className="w-3 h-3 mr-1" /> VISTA PARA CLIENTE
              </Badge>
            ) : (
              <Badge className="bg-amber-400/90 text-amber-950 border-amber-300 font-bold">
                PREVIEW · NO PRODUCTIVO
              </Badge>
            )}
            {!isPublic && (
              <Button
                onClick={() => setShowShare(true)}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/30 font-semibold backdrop-blur-sm"
                data-testid="open-share-modal-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Compartir Demo
              </Button>
            )}
            <Button
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-lg"
              data-testid="download-propuesta-pdf-btn"
            >
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              Descargar Propuesta Ejecutiva (PDF)
            </Button>
          </div>
        </div>
      </header>

      {/* Stepper */}
      <div className="max-w-7xl mx-auto px-6 py-5">
        <div className="flex items-center gap-1 overflow-x-auto pb-2" data-testid="alnap-stepper">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = i === step;
            const done = i < step;
            return (
              <button
                key={s.id}
                onClick={() => goTo(i)}
                data-testid={`step-${s.key}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                  active
                    ? "bg-[#193966] text-white shadow-md scale-105"
                    : done
                    ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                    : "bg-white text-slate-500 hover:bg-slate-100 border border-slate-200"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {i + 1}. {s.title}
                {i < STEPS.length - 1 && <ChevronRight className="w-3 h-3 opacity-50 ml-1" />}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="mt-4">
          {step === 0 && <Intro onNext={next} onDownload={handleDownloadPDF} downloading={downloading} />}
          {step === 1 && <Dashboard />}
          {step === 2 && <Padron />}
          {step === 3 && <PanelQAsesores />}
          {step === 4 && <Generador onDownloadPDF={handleDownloadPDF} isPublic={isPublic} publicToken={publicToken} />}
          {step === 5 && <EmailEmpleado />}
          {step === 6 && <PortalEmpleado selected={selectedAseguradora} onSelect={setSelectedAseguradora} onNext={next} />}
          {step === 7 && <FirmaOTP canvasRef={canvasRef} otp={otp} setOtp={setOtp} signed={signed} setSigned={setSigned} selected={selectedAseguradora} onNext={next} />}
          {step === 8 && <Constancia selected={selectedAseguradora} />}
        </div>

        {/* Nav */}
        <div className="flex justify-between mt-6 pb-12">
          <Button onClick={prev} disabled={step === 0} variant="outline" data-testid="step-prev-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Anterior
          </Button>
          <span className="text-xs text-slate-500 self-center">Paso {step + 1} de {STEPS.length}</span>
          <Button onClick={next} disabled={step === STEPS.length - 1} className="bg-[#193966] hover:bg-[#0F2240] text-white font-semibold disabled:opacity-40" data-testid="step-next-btn">
            Siguiente <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Modal de Share Links (sólo modo admin) */}
      {!isPublic && (
        <ShareLinksModal
          open={showShare}
          onOpenChange={setShowShare}
          getAuthHeaders={getAuthHeaders}
        />
      )}
    </div>
  );
}

// ─────────── STEPS COMPONENTS ───────────

function Intro({ onNext, onDownload, downloading }) {
  return (
    <Card className="border-0 shadow-2xl overflow-hidden">
      <div className="grid md:grid-cols-2">
        <div className="bg-gradient-to-br from-[#193966] via-[#0F2240] to-slate-900 text-white p-10 flex flex-col justify-center">
          <Sparkles className="w-10 h-10 text-emerald-300 mb-4" />
          <h2 className="text-3xl font-bold leading-tight mb-3">
            Automatice el ciclo de cotización de seguros vehiculares
          </h2>
          <p className="text-blue-100/90 text-sm leading-relaxed mb-5">
            Una solución de extremo a extremo para que ALNAP gestione las cotizaciones de sus empleados y flotillas con trazabilidad legal completa, firma digital y dashboard ejecutivo.
          </p>
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white">Trazabilidad 100%</Badge>
            <Badge className="bg-amber-500 hover:bg-amber-500 text-white">Ley 126-02</Badge>
            <Badge className="bg-blue-500 hover:bg-blue-500 text-white">1-Click</Badge>
            <Badge className="bg-purple-500 hover:bg-purple-500 text-white">Multi-aseguradora</Badge>
          </div>
        </div>
        <div className="p-10 bg-white">
          <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-2">Recorrido del demo</p>
          <h3 className="text-xl font-bold text-slate-800 mb-4">7 pantallas navegables</h3>
          <ol className="space-y-2.5 text-sm text-slate-600 mb-6">
            {STEPS.slice(1).map((s) => (
              <li key={s.id} className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span><b className="text-slate-800">{s.title}</b></span>
              </li>
            ))}
          </ol>
          <div className="flex gap-2">
            <Button onClick={onNext} className="flex-1 bg-[#80BC00] hover:bg-[#6ea700] text-white font-bold" data-testid="intro-start-tour-btn">
              Iniciar Tour <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button onClick={onDownload} disabled={downloading} variant="outline" className="border-[#193966] text-[#193966]" data-testid="intro-download-pdf-btn">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-4 italic">
            Datos mostrados son ficticios con fines demostrativos. El módulo real conectará con el padrón cargado por ALNAP.
          </p>
        </div>
      </div>
    </Card>
  );
}

function Dashboard() {
  const kpis = [
    { label: "Cotizaciones del mes", value: "47", trend: "+12%", icon: FileText, color: "from-blue-500 to-indigo-600" },
    { label: "Firmadas", value: "32", trend: "68%", icon: FileCheck2, color: "from-emerald-500 to-green-600" },
    { label: "Pendientes de empleado", value: "11", trend: "—", icon: Clock, color: "from-amber-500 to-orange-600" },
    { label: "Prima total gestionada", value: "DOP 2.34M", trend: "+18%", icon: TrendingUp, color: "from-purple-500 to-fuchsia-600" },
  ];
  const recientes = [
    { empleado: "María Fernández", aseguradora: "La Colonial", prima: "DOP 43,500", estado: "Firmada", fecha: "Hoy 09:42" },
    { empleado: "Carlos Martínez", aseguradora: "Mapfre BHD", prima: "DOP 40,960", estado: "Vista", fecha: "Ayer 16:11" },
    { empleado: "Ana L. Peguero", aseguradora: "—", prima: "—", estado: "Enviada", fecha: "Ayer 14:30" },
    { empleado: "Rafael Sánchez", aseguradora: "Pepín", prima: "DOP 69,300", estado: "Firmada", fecha: "12/05 11:25" },
    { empleado: "Flotilla #12", aseguradora: "La Colonial", prima: "DOP 55,500", estado: "Aprobada", fecha: "10/05 10:00" },
  ];
  const colorEstado = (e) => ({
    "Firmada": "bg-emerald-100 text-emerald-700 border-emerald-300",
    "Aprobada": "bg-blue-100 text-blue-700 border-blue-300",
    "Vista": "bg-amber-100 text-amber-700 border-amber-300",
    "Enviada": "bg-slate-100 text-slate-700 border-slate-300",
  }[e] || "bg-slate-100 text-slate-700");

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <Card key={k.label} className={`border-0 shadow-md bg-gradient-to-br ${k.color} text-white`}>
              <CardContent className="p-5">
                <div className="flex justify-between mb-1">
                  <p className="text-[10px] uppercase tracking-wider font-bold opacity-90">{k.label}</p>
                  <Icon className="w-4 h-4 opacity-80" />
                </div>
                <p className="text-3xl font-bold">{k.value}</p>
                <p className="text-[11px] opacity-80 mt-1">{k.trend}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="py-3 px-5 border-b bg-slate-50/80">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#193966]" />
            Cotizaciones recientes
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-[#193966] text-white text-xs">
              <tr>
                <th className="px-4 py-2.5 text-left">Empleado</th>
                <th className="px-4 py-2.5 text-left">Aseguradora elegida</th>
                <th className="px-4 py-2.5 text-right">Prima anual</th>
                <th className="px-4 py-2.5 text-center">Estado</th>
                <th className="px-4 py-2.5 text-right">Última actividad</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recientes.map((r, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-800">{r.empleado}</td>
                  <td className="px-4 py-3 text-slate-600">{r.aseguradora}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{r.prima}</td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant="outline" className={colorEstado(r.estado)}>{r.estado}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-slate-500">{r.fecha}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function Padron() {
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="py-4 px-5 border-b bg-gradient-to-r from-slate-50 to-blue-50/40">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-base font-bold text-slate-800 flex items-center gap-2">
              <Car className="w-5 h-5 text-[#193966]" />
              Padrón Anual de Vehículos
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">Carga anual del Excel de ALNAP · 1,247 vehículos registrados · Período 2026</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="text-xs">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Plantilla Excel
            </Button>
            <Button size="sm" className="bg-[#80BC00] hover:bg-[#6ea700] text-xs">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Importar Excel anual
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="px-5 py-3 bg-blue-50/40 border-b border-blue-100">
          <Input placeholder="Buscar por empleado, cédula o placa…" className="h-8 text-sm bg-white" />
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700 text-xs">
            <tr>
              <th className="px-3 py-2 text-left">ID</th>
              <th className="px-3 py-2 text-left">Empleado / Titular</th>
              <th className="px-3 py-2 text-left">Cédula / RNC</th>
              <th className="px-3 py-2 text-left">Vehículo</th>
              <th className="px-3 py-2 text-right">Valor</th>
              <th className="px-3 py-2 text-center">Tipo</th>
              <th className="px-3 py-2 text-center">Acción</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {DEMO_VEHICULOS.map((v) => (
              <tr key={v.id} className="hover:bg-slate-50/80">
                <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{v.id}</td>
                <td className="px-3 py-2.5 font-medium text-slate-800">{v.empleado}</td>
                <td className="px-3 py-2.5 text-slate-600 text-xs">{v.cedula}</td>
                <td className="px-3 py-2.5 text-slate-700">{v.marca} {v.modelo} <span className="text-slate-400">{v.anio}</span></td>
                <td className="px-3 py-2.5 text-right font-mono text-slate-700">DOP {v.valor.toLocaleString("es-DO")}</td>
                <td className="px-3 py-2.5 text-center">
                  <Badge variant="outline" className={v.tipo === "Flotilla" ? "bg-purple-100 text-purple-700 border-purple-300" : "bg-blue-100 text-blue-700 border-blue-300"}>{v.tipo}</Badge>
                </td>
                <td className="px-3 py-2.5 text-center">
                  <Button size="sm" variant="outline" className="text-xs h-7">
                    <Wand2 className="w-3 h-3 mr-1" /> Cotizar
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-5 py-2 text-xs text-slate-500 border-t border-slate-100 bg-slate-50">
          Mostrando 5 de 1,247 vehículos del padrón 2026 · <a href="#" className="text-[#193966] font-semibold">Ver todos →</a>
        </div>
      </CardContent>
    </Card>
  );
}

// ─────────── PANEL ADMINISTRATIVO QASESORES ───────────
function PanelQAsesores() {
  const [tab, setTab] = useState("aseguradoras");
  const tabs = [
    { id: "aseguradoras", label: "Aseguradoras", icon: ShieldCheck },
    { id: "coberturas", label: "Coberturas", icon: ShieldAlert },
    { id: "tasas", label: "Tasas Vigentes", icon: Percent },
    { id: "padron_qa", label: "Sincronización Padrón", icon: Database },
  ];
  return (
    <div className="space-y-4">
      {/* Banner explicativo */}
      <Card className="border-0 shadow-md bg-gradient-to-br from-[#193966] to-[#0F2240] text-white overflow-hidden">
        <CardContent className="p-5 flex items-center gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-xl bg-emerald-400/20 backdrop-blur-sm flex items-center justify-center border border-emerald-300/30 flex-shrink-0">
            <Settings className="w-6 h-6 text-emerald-300" />
          </div>
          <div className="flex-1 min-w-[260px]">
            <p className="text-xs uppercase tracking-widest text-emerald-300 font-bold mb-1">PANEL EXCLUSIVO QASESORES</p>
            <h2 className="text-lg font-bold">Configuración y mantenimiento del Portal ALNAP</h2>
            <p className="text-xs text-blue-100/80 mt-1">Desde aquí ustedes (no ALNAP) controlan las aseguradoras disponibles, las coberturas que ofrece cada una y las tasas vigentes con las que se cotiza en cada momento.</p>
          </div>
          <Badge className="bg-amber-400 text-amber-950 font-bold flex-shrink-0">
            <Lock className="w-3 h-3 mr-1" /> Solo admins
          </Badge>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto" data-testid="panel-qa-tabs">
        {tabs.map((t) => {
          const Ic = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`panel-qa-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors ${
                active ? "border-[#80BC00] text-[#193966]" : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
              }`}
            >
              <Ic className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "aseguradoras" && <PanelAseguradoras />}
      {tab === "coberturas" && <PanelCoberturas />}
      {tab === "tasas" && <PanelTasas />}
      {tab === "padron_qa" && <PanelPadronSync />}
    </div>
  );
}

function PanelAseguradoras() {
  const aseguradoras = [
    { nombre: "Mapfre BHD Seguros", contacto: "Lic. Roberto Mejía", email: "rmejia@mapfre.com.do", tel: "809-555-1100", activa: true, polizas: 142, vigencia: "01/01/2026 — 31/12/2026" },
    { nombre: "Seguros La Colonial", contacto: "Sra. Lourdes Vásquez", email: "lvasquez@lacolonial.do", tel: "809-555-2200", activa: true, polizas: 89, vigencia: "01/01/2026 — 31/12/2026" },
    { nombre: "Seguros Pepín", contacto: "Lic. Andrés Peña", email: "apena@pepin.com.do", tel: "809-555-3300", activa: true, polizas: 67, vigencia: "01/01/2026 — 31/12/2026" },
    { nombre: "Banreservas Seguros", contacto: "Sr. Carlos Rosario", email: "crosario@bdr.com.do", tel: "809-555-4400", activa: false, polizas: 0, vigencia: "Pausada — sin contrato 2026" },
    { nombre: "MAPFRE Multinacional", contacto: "Sr. Luis Hernández", email: "lhernandez@mapfremulti.do", tel: "809-555-5500", activa: false, polizas: 0, vigencia: "Pausada — tasas en negociación" },
  ];
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="py-3 px-4 border-b bg-slate-50 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-bold flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#193966]" /> Aseguradoras configuradas para ALNAP
        </CardTitle>
        <Button size="sm" className="bg-[#80BC00] hover:bg-[#6ea700] text-xs h-8" data-testid="panel-add-aseguradora">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Agregar aseguradora
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-[#193966] text-white text-xs">
            <tr>
              <th className="px-4 py-2.5 text-left">Aseguradora</th>
              <th className="px-4 py-2.5 text-left">Contacto comercial</th>
              <th className="px-4 py-2.5 text-left">Vigencia de contrato</th>
              <th className="px-4 py-2.5 text-center">Pólizas activas</th>
              <th className="px-4 py-2.5 text-center">Estado</th>
              <th className="px-4 py-2.5 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {aseguradoras.map((a, i) => (
              <tr key={i} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <p className="font-bold text-slate-800">{a.nombre}</p>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  <p className="font-medium">{a.contacto}</p>
                  <p className="text-slate-400">{a.email}</p>
                  <p className="text-slate-400">{a.tel}</p>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">{a.vigencia}</td>
                <td className="px-4 py-3 text-center font-mono font-bold text-[#193966]">{a.polizas}</td>
                <td className="px-4 py-3 text-center">
                  {a.activa ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px]">Activa</Badge>
                  ) : (
                    <Badge className="bg-slate-200 text-slate-600 border-slate-300 text-[10px]">Pausada</Badge>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex justify-center gap-1">
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0"><Pencil className="w-3 h-3" /></Button>
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-rose-600 border-rose-200"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function PanelCoberturas() {
  const [aseguradora, setAseguradora] = useState("Seguros La Colonial");
  const coberturas = {
    "Seguros La Colonial": [
      { nombre: "Responsabilidad Civil", limite: "DOP 5,000,000", deducible: "—", obligatoria: true },
      { nombre: "Daños propios al vehículo", limite: "Valor declarado", deducible: "DOP 30,000", obligatoria: true },
      { nombre: "Robo total", limite: "Valor declarado", deducible: "10% del siniestro", obligatoria: true },
      { nombre: "Asistencia vial 24/7", limite: "Sin límite eventos", deducible: "—", obligatoria: false },
      { nombre: "Auto sustituto", limite: "5 días", deducible: "—", obligatoria: false },
      { nombre: "Cristales y vidrios", limite: "DOP 50,000", deducible: "DOP 5,000", obligatoria: false },
      { nombre: "Gastos médicos ocupantes", limite: "DOP 200,000", deducible: "—", obligatoria: true },
    ],
    "Mapfre BHD Seguros": [
      { nombre: "Responsabilidad Civil", limite: "DOP 5,000,000", deducible: "—", obligatoria: true },
      { nombre: "Daños propios", limite: "Valor declarado", deducible: "DOP 25,000", obligatoria: true },
      { nombre: "Robo total", limite: "Valor declarado", deducible: "10%", obligatoria: true },
      { nombre: "Asistencia 24/7", limite: "Sin límite", deducible: "—", obligatoria: false },
      { nombre: "Auto sustituto", limite: "7 días", deducible: "—", obligatoria: false },
      { nombre: "Gastos médicos", limite: "DOP 250,000", deducible: "—", obligatoria: true },
    ],
    "Seguros Pepín": [
      { nombre: "Responsabilidad Civil", limite: "DOP 4,000,000", deducible: "—", obligatoria: true },
      { nombre: "Daños propios", limite: "Valor declarado", deducible: "DOP 20,000", obligatoria: true },
      { nombre: "Robo total", limite: "Valor declarado", deducible: "10%", obligatoria: true },
      { nombre: "Asistencia vial", limite: "Sin límite", deducible: "—", obligatoria: false },
      { nombre: "Auto sustituto", limite: "7 días", deducible: "—", obligatoria: false },
    ],
  };
  const opts = Object.keys(coberturas);
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="py-3 px-4 border-b bg-slate-50">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="text-sm font-bold flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-[#193966]" /> Coberturas por aseguradora
          </CardTitle>
          <select
            value={aseguradora}
            onChange={(e) => setAseguradora(e.target.value)}
            className="text-xs border border-slate-300 rounded px-2 py-1.5 bg-white"
            data-testid="panel-coberturas-select"
          >
            {opts.map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700 text-xs">
            <tr>
              <th className="px-4 py-2 text-left">Cobertura</th>
              <th className="px-4 py-2 text-left">Límite / Suma asegurada</th>
              <th className="px-4 py-2 text-left">Deducible</th>
              <th className="px-4 py-2 text-center">Obligatoria</th>
              <th className="px-4 py-2 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {coberturas[aseguradora].map((c, i) => (
              <tr key={i} className="hover:bg-slate-50">
                <td className="px-4 py-2.5 font-medium text-slate-800">{c.nombre}</td>
                <td className="px-4 py-2.5 text-slate-700 font-mono text-xs">{c.limite}</td>
                <td className="px-4 py-2.5 text-slate-700 font-mono text-xs">{c.deducible}</td>
                <td className="px-4 py-2.5 text-center">
                  {c.obligatoria ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" />
                  ) : (
                    <span className="text-slate-300 text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-center">
                  <div className="flex justify-center gap-1">
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0"><Pencil className="w-3 h-3" /></Button>
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-rose-600 border-rose-200"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-between">
          <p className="text-xs text-slate-500">Las coberturas obligatorias se aplican siempre. Las opcionales aparecen en el comparativo del empleado.</p>
          <Button size="sm" className="bg-[#80BC00] hover:bg-[#6ea700] text-xs h-8" data-testid="panel-add-cobertura">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Agregar cobertura
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PanelTasas() {
  const tasas = [
    { aseguradora: "Mapfre BHD Seguros", tipo: "Vehículo personal — Empleado", anio: "2020+", tasa: 3.2, deducible: 25000, vigencia: "Vigente · vence 31/12/2026" },
    { aseguradora: "Mapfre BHD Seguros", tipo: "Vehículo personal — Empleado", anio: "2015-2019", tasa: 3.6, deducible: 30000, vigencia: "Vigente · vence 31/12/2026" },
    { aseguradora: "Seguros La Colonial", tipo: "Vehículo personal — Empleado", anio: "2020+", tasa: 3.0, deducible: 30000, vigencia: "Vigente · vence 31/12/2026" },
    { aseguradora: "Seguros La Colonial", tipo: "Vehículo personal — Empleado", anio: "2015-2019", tasa: 3.4, deducible: 35000, vigencia: "Vigente · vence 31/12/2026" },
    { aseguradora: "Seguros La Colonial", tipo: "Flotilla corporativa", anio: "Todos", tasa: 2.8, deducible: 40000, vigencia: "Vigente · vence 31/12/2026" },
    { aseguradora: "Seguros Pepín", tipo: "Vehículo personal — Empleado", anio: "2020+", tasa: 3.5, deducible: 20000, vigencia: "Vigente · vence 31/12/2026" },
    { aseguradora: "Seguros Pepín", tipo: "Flotilla corporativa", anio: "Todos", tasa: 3.1, deducible: 35000, vigencia: "Vigente · vence 31/12/2026" },
  ];
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="py-3 px-4 border-b bg-gradient-to-r from-emerald-50 to-slate-50">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <Percent className="w-4 h-4 text-emerald-600" /> Tasas vigentes para cotización
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">Las tasas se aplican automáticamente cuando ALNAP genera una cotización. ALNAP NO ve estas configuraciones.</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="text-xs h-8" data-testid="panel-tasas-historial">
              <Clock className="w-3.5 h-3.5 mr-1.5" /> Ver histórico
            </Button>
            <Button size="sm" className="bg-[#80BC00] hover:bg-[#6ea700] text-xs h-8" data-testid="panel-add-tasa">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Nueva tasa
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700 text-xs">
            <tr>
              <th className="px-3 py-2 text-left">Aseguradora</th>
              <th className="px-3 py-2 text-left">Tipo / Segmento</th>
              <th className="px-3 py-2 text-left">Año modelo</th>
              <th className="px-3 py-2 text-right">Tasa %</th>
              <th className="px-3 py-2 text-right">Deducible base</th>
              <th className="px-3 py-2 text-left">Vigencia</th>
              <th className="px-3 py-2 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {tasas.map((t, i) => (
              <tr key={i} className="hover:bg-slate-50">
                <td className="px-3 py-2.5 font-medium text-slate-800 text-xs">{t.aseguradora}</td>
                <td className="px-3 py-2.5 text-slate-700 text-xs">{t.tipo}</td>
                <td className="px-3 py-2.5 text-slate-700 text-xs">{t.anio}</td>
                <td className="px-3 py-2.5 text-right">
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 font-mono font-bold text-[11px]">{t.tasa}%</Badge>
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-700">DOP {t.deducible.toLocaleString("es-DO")}</td>
                <td className="px-3 py-2.5 text-xs text-emerald-700">{t.vigencia}</td>
                <td className="px-3 py-2.5 text-center">
                  <div className="flex justify-center gap-1">
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0"><Pencil className="w-3 h-3" /></Button>
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-rose-600 border-rose-200"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-3 bg-blue-50 border-t border-blue-100 flex items-center gap-2 text-xs text-blue-900">
          <AlertCircle className="w-3.5 h-3.5" />
          <span>Cualquier cambio a una tasa queda registrado en el <b>histórico</b> con timestamp y usuario que lo realizó (auditoría completa).</span>
        </div>
      </CardContent>
    </Card>
  );
}

function PanelPadronSync() {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="py-3 px-4 border-b">
          <CardTitle className="text-sm font-bold flex items-center gap-2">
            <Database className="w-4 h-4 text-[#193966]" /> Estado del padrón
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-2 text-sm">
          <Field label="Total vehículos" value="1,247" bold />
          <Field label="Empleados con vehículo" value="1,103" />
          <Field label="Vehículos de flotilla" value="144" />
          <Field label="Última carga del Excel" value="03/01/2026 09:14 — ALNAP RRHH" />
          <Field label="Próximo refresh sugerido" value="01/04/2026 (trimestral)" />
          <Field label="Vehículos con cotización pendiente" value="89" bold />
        </CardContent>
      </Card>
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="py-3 px-4 border-b">
          <CardTitle className="text-sm font-bold flex items-center gap-2">
            <Settings className="w-4 h-4 text-[#193966]" /> Acciones de mantenimiento
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-2">
          <Button variant="outline" className="w-full justify-start text-xs h-9">
            <FileText className="w-3.5 h-3.5 mr-2" /> Solicitar nuevo Excel a ALNAP
          </Button>
          <Button variant="outline" className="w-full justify-start text-xs h-9">
            <Download className="w-3.5 h-3.5 mr-2" /> Exportar padrón actual
          </Button>
          <Button variant="outline" className="w-full justify-start text-xs h-9">
            <Mail className="w-3.5 h-3.5 mr-2" /> Reenviar recordatorios masivos
          </Button>
          <Button variant="outline" className="w-full justify-start text-xs h-9">
            <Activity className="w-3.5 h-3.5 mr-2" /> Ver bitácora de auditoría
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}


function Generador({ onDownloadPDF, isPublic, publicToken }) {
  const v = DEMO_VEHICULOS[0];
  const handlePreview = async () => {
    // Reusa el endpoint del PDF ejecutivo como ejemplo de previsualización
    try {
      const url = isPublic
        ? `${API_URL}/api/alnap/public/${publicToken}/propuesta.pdf`
        : `${API_URL}/api/alnap/propuesta.pdf`;
      // En modo admin necesitamos headers; abrimos en nueva pestaña con descarga
      if (onDownloadPDF) {
        await onDownloadPDF();
        toast.info("PDF de demostración descargado — éste sería el comparativo formal");
      } else {
        window.open(url, "_blank");
      }
    } catch {
      toast.error("No se pudo abrir el preview");
    }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-1 border-slate-200 shadow-sm">
        <CardHeader className="py-3 px-4 border-b">
          <CardTitle className="text-sm font-bold flex items-center gap-2">
            <Car className="w-4 h-4 text-[#193966]" />
            Vehículo seleccionado
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-2 text-sm">
          <Field label="Empleado" value={v.empleado} />
          <Field label="Cédula" value={v.cedula} />
          <Field label="Email" value={v.email} />
          <Field label="Vehículo" value={`${v.marca} ${v.modelo} ${v.anio}`} />
          <Field label="Chasis" value={v.chasis} mono />
          <Field label="Valor asegurado" value={`DOP ${v.valor.toLocaleString("es-DO")}`} bold />
        </CardContent>
      </Card>

      <Card className="lg:col-span-2 border-slate-200 shadow-sm">
        <CardHeader className="py-3 px-4 border-b bg-gradient-to-r from-emerald-50 to-blue-50/40">
          <CardTitle className="text-sm font-bold flex items-center justify-between">
            <span className="flex items-center gap-2"><Wand2 className="w-4 h-4 text-[#80BC00]" /> Cotización 1-click · 3 aseguradoras</span>
            <Badge className="bg-emerald-500 text-white">Tasas vigentes QAsesores</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          {DEMO_ASEGURADORAS.map((a) => (
            <div key={a.id} className={`rounded-lg border-2 p-3 ${a.destacado ? "border-emerald-400 bg-emerald-50/40" : "border-slate-200"}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${a.color} flex items-center justify-center text-white font-bold text-xs`}>
                    {a.nombre.split(" ")[0].slice(0, 3).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-800">{a.nombre}</p>
                    <p className="text-xs text-slate-500">Tasa {a.tasa}% · Deducible DOP {a.deducible.toLocaleString("es-DO")}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500">Prima anual</p>
                  <p className="text-lg font-bold text-[#193966]">DOP {a.primaAnual.toLocaleString("es-DO")}</p>
                </div>
              </div>
            </div>
          ))}
          <div className="flex gap-2 pt-2">
            <Button className="flex-1 bg-[#80BC00] hover:bg-[#6ea700] text-white font-bold" onClick={() => toast.success("Email enviado a María Fernández (demo)")} data-testid="generador-enviar-btn">
              <Mail className="w-4 h-4 mr-2" /> Enviar comparativo al empleado
            </Button>
            <Button variant="outline" onClick={handlePreview} data-testid="generador-preview-pdf-btn">
              <Eye className="w-4 h-4 mr-2" /> Previsualizar PDF
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function EmailEmpleado() {
  return (
    <Card className="border-slate-200 shadow-md max-w-3xl mx-auto">
      <CardHeader className="py-3 px-4 border-b bg-slate-50">
        <CardTitle className="text-sm font-bold flex items-center gap-2">
          <MailCheck className="w-4 h-4 text-[#193966]" />
          Email enviado · Visualización
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="px-5 py-3 border-b text-xs text-slate-600 space-y-1 bg-slate-50/60">
          <p><span className="text-slate-400">De:</span> <b>QAsesores · ALNAP</b> &lt;cotizaciones@alnap.com.do&gt;</p>
          <p><span className="text-slate-400">Para:</span> mfernandez@alnap.com.do</p>
          <p><span className="text-slate-400">Asunto:</span> <b>Tu cotización de seguro vehicular está lista — María Fernández</b></p>
        </div>
        <div className="p-8 bg-white">
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-[#193966] to-[#0F2240] flex items-center justify-center mb-3 shadow-lg">
              <Send className="w-7 h-7 text-emerald-300" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800">Hola María 👋</h2>
            <p className="text-slate-500 text-sm mt-1">Tu comparativo de seguro vehicular ya está listo</p>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed mb-4">
            Como parte del beneficio corporativo de <b>ALNAP</b>, te enviamos la cotización de seguro para tu <b>Toyota Corolla 2023</b>. Negociamos tasas preferenciales con tres aseguradoras de primera línea.
          </p>
          <div className="bg-blue-50 border-l-4 border-[#193966] p-4 rounded mb-5 text-sm">
            <p className="font-semibold text-[#193966]">Tu acción</p>
            <p className="text-slate-600 mt-1">Hacé click en el botón debajo para comparar las opciones y elegir la aseguradora con la que querés contratar.</p>
          </div>
          <div className="text-center mb-6">
            <button className="px-8 py-3 bg-[#80BC00] hover:bg-[#6ea700] text-white font-bold rounded-lg shadow-lg">
              Ver mi cotización →
            </button>
            <p className="text-xs text-slate-400 mt-3">Link único y seguro · expira en 30 días</p>
          </div>
          <div className="text-xs text-slate-500 border-t pt-4 leading-relaxed">
            Este email fue generado automáticamente por el Portal de Cotizaciones Corporativas. Cada interacción queda registrada para tu protección. — ALNAP & QAsesores
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PortalEmpleado({ selected, onSelect, onNext }) {
  return (
    <div className="max-w-5xl mx-auto">
      <Card className="border-slate-200 shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-[#193966] to-[#0F2240] text-white px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="text-xs uppercase tracking-widest text-emerald-300 font-semibold">[LOGO ALNAP] · Portal Empleado</p>
              <h2 className="text-lg font-bold">Hola María Fernández</h2>
              <p className="text-xs text-blue-100/80">Tu cotización para Toyota Corolla 2023 · Valor DOP 1,450,000</p>
            </div>
            <Badge className="bg-amber-400 text-amber-950 font-bold"><Clock className="w-3 h-3 mr-1" />Expira en 29 días</Badge>
          </div>
        </div>
        <CardContent className="p-5">
          <p className="text-sm text-slate-600 mb-4">
            Te presentamos las 3 mejores opciones que negociamos con cada aseguradora. <b>Elegí la que mejor se ajuste a tus necesidades</b>:
          </p>
          <div className="grid md:grid-cols-3 gap-3">
            {DEMO_ASEGURADORAS.map((a) => {
              const isSel = selected === a.id;
              return (
                <button
                  key={a.id}
                  onClick={() => { onSelect(a.id); toast.success(`Aseguradora seleccionada: ${a.nombre}`); }}
                  data-testid={`portal-select-${a.id}`}
                  className={`relative text-left p-4 rounded-xl border-2 transition-all ${
                    isSel ? "border-emerald-500 ring-4 ring-emerald-200 bg-emerald-50" : "border-slate-200 hover:border-[#193966] bg-white"
                  }`}
                >
                  {a.destacado && (
                    <Badge className="absolute -top-2 left-3 bg-amber-400 text-amber-950 text-[10px] font-bold">RECOMENDADA</Badge>
                  )}
                  {isSel && (
                    <CheckCircle2 className="absolute top-3 right-3 w-5 h-5 text-emerald-500" />
                  )}
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${a.color} flex items-center justify-center text-white font-bold text-sm mb-3`}>
                    {a.nombre.split(" ")[0].slice(0, 3).toUpperCase()}
                  </div>
                  <h3 className="text-sm font-bold text-slate-800 mb-2">{a.nombre}</h3>
                  <p className="text-2xl font-bold text-[#193966] mb-1">DOP {a.primaAnual.toLocaleString("es-DO")}</p>
                  <p className="text-xs text-slate-500 mb-3">Prima anual · Deducible {a.deducible.toLocaleString("es-DO")}</p>
                  <ul className="space-y-1 text-xs text-slate-600 mb-4">
                    {a.coberturas.map((c, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                  <div className={`text-center text-xs font-bold py-2 rounded ${isSel ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-600"}`}>
                    {isSel ? "✓ SELECCIONADA" : "Elegir esta opción"}
                  </div>
                </button>
              );
            })}
          </div>
          {selected && (
            <div className="mt-5 p-4 bg-emerald-50 border-2 border-emerald-300 rounded-lg flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm">
                <MousePointerClick className="w-5 h-5 text-emerald-600" />
                <span>Has elegido <b>{DEMO_ASEGURADORAS.find(a => a.id === selected)?.nombre}</b>. Continúa para confirmar y firmar.</span>
              </div>
              <Button onClick={onNext} className="bg-emerald-600 hover:bg-emerald-700" data-testid="portal-continue-btn">
                Confirmar y firmar <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function FirmaOTP({ canvasRef, otp, setOtp, signed, setSigned, selected, onNext }) {
  const asegurador = DEMO_ASEGURADORAS.find(a => a.id === selected) || DEMO_ASEGURADORAS[1];
  const [drawing, setDrawing] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#193966";
  }, [canvasRef]);

  const start = (e) => {
    setDrawing(true);
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const ctx = c.getContext("2d");
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawing) return;
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const ctx = c.getContext("2d");
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
    setSigned(true);
  };
  const stop = () => setDrawing(false);
  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setSigned(false);
  };

  const canFinish = signed && otp.length === 6;

  return (
    <Card className="border-slate-200 shadow-md max-w-3xl mx-auto">
      <CardHeader className="py-3 px-4 border-b bg-gradient-to-r from-[#193966] to-[#0F2240] text-white">
        <CardTitle className="text-sm font-bold flex items-center gap-2">
          <FileSignature className="w-4 h-4 text-emerald-300" />
          Confirmar y firmar selección
        </CardTitle>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
          <p className="font-semibold text-[#193966] mb-1">Resumen de tu selección</p>
          <ul className="text-xs text-slate-700 space-y-0.5">
            <li>• Vehículo: <b>Toyota Corolla 2023</b></li>
            <li>• Aseguradora elegida: <b>{asegurador.nombre}</b></li>
            <li>• Prima anual: <b>DOP {asegurador.primaAnual.toLocaleString("es-DO")}</b></li>
            <li>• Deducible: <b>DOP {asegurador.deducible.toLocaleString("es-DO")}</b></li>
          </ul>
        </div>

        <div>
          <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
            <Signature className="w-4 h-4" /> Firmá con tu dedo o mouse:
          </p>
          <div className="border-2 border-dashed border-slate-300 rounded-lg bg-slate-50">
            <canvas
              ref={canvasRef}
              width={600}
              height={150}
              className="w-full h-[150px] cursor-crosshair touch-none"
              onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop}
              onTouchStart={start} onTouchMove={draw} onTouchEnd={stop}
              data-testid="firma-canvas"
            />
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-xs text-slate-400">{signed ? "✓ Firma capturada" : "Esperando firma…"}</span>
            <button onClick={clear} className="text-xs text-rose-600 font-semibold hover:underline" data-testid="firma-clear-btn">Limpiar firma</button>
          </div>
        </div>

        <div>
          <p className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-emerald-600" /> Código OTP (6 dígitos)
          </p>
          <p className="text-xs text-slate-500 mb-2">Enviamos un código a tu correo <b>mfernandez@alnap.com.do</b> y celular ****-4521</p>
          <Input
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="• • • • • •"
            className="text-center text-2xl tracking-[0.5em] font-bold h-12 max-w-[280px]"
            data-testid="firma-otp-input"
          />
          <p className="text-xs text-slate-400 mt-1">Demo: usá <span className="font-mono bg-slate-100 px-1 rounded">123456</span></p>
        </div>

        <Button
          onClick={() => { if (canFinish) { toast.success("Aprobación registrada ✓"); onNext(); } else toast.error("Completá firma y OTP"); }}
          disabled={!canFinish}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11"
          data-testid="firma-confirm-btn"
        >
          <CheckCircle2 className="w-4 h-4 mr-2" /> Firmar y aprobar
        </Button>
      </CardContent>
    </Card>
  );
}

function Constancia({ selected }) {
  const asegurador = DEMO_ASEGURADORAS.find(a => a.id === selected) || DEMO_ASEGURADORAS[1];
  const now = new Date();
  return (
    <Card className="border-2 border-emerald-400 shadow-2xl max-w-3xl mx-auto bg-white">
      <CardHeader className="py-4 px-5 border-b bg-gradient-to-r from-emerald-500 to-green-600 text-white">
        <CardTitle className="flex items-center gap-2 text-base font-bold">
          <FileCheck2 className="w-5 h-5" /> Constancia de Aprobación · Documento Inmutable
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-3 text-sm">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 mb-3">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">Aprobación registrada exitosamente</h2>
          <p className="text-xs text-slate-500 mt-1">Esta constancia tiene valor probatorio según la Ley 126-02</p>
        </div>

        <div className="bg-slate-50 rounded-lg p-4 space-y-1.5 text-xs border border-slate-200 font-mono">
          <Row k="Folio" v={`PCC-2026-${String(Math.floor(Math.random() * 999) + 1).padStart(4, "0")}`} />
          <Row k="Empleado" v="María Fernández Castillo" />
          <Row k="Cédula" v="001-1234567-8" />
          <Row k="Vehículo" v="Toyota Corolla 2023 · Chasis JTDB...8451" />
          <Row k="Aseguradora elegida" v={asegurador.nombre} />
          <Row k="Prima anual" v={`DOP ${asegurador.primaAnual.toLocaleString("es-DO")}`} />
          <Row k="Fecha y hora" v={now.toLocaleString("es-DO")} />
          <Row k="IP de firma" v="186.144.27.103" />
          <Row k="Dispositivo" v="Chrome 132 · macOS" />
          <Row k="OTP validado" v="✓ 123456" />
          <Row k="Hash SHA-256" v="a7f3b8...d1e94c (sello de integridad)" />
        </div>

        <div className="flex gap-2 pt-2">
          <Button className="flex-1 bg-[#193966] hover:bg-[#0F2240] text-white font-bold">
            <Download className="w-4 h-4 mr-2" /> Descargar Constancia (PDF)
          </Button>
          <Button variant="outline" className="border-[#193966] text-[#193966]">
            <Mail className="w-4 h-4 mr-2" /> Enviar a ALNAP
          </Button>
        </div>

        <div className="text-center text-xs text-slate-400 pt-3 border-t italic">
          QAsesores Corredores de Seguros y Reaseguros SRL · Para ALNAP · Documento generado automáticamente
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, value, mono, bold }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-slate-500">{label}</span>
      <span className={`${mono ? "font-mono" : ""} ${bold ? "font-bold text-[#193966]" : "text-slate-700"} text-right`}>{value}</span>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3 py-0.5">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-800 font-bold text-right break-all">{v}</span>
    </div>
  );
}

// ─────────── SHARE LINKS MODAL ───────────
function ShareLinksModal({ open, onOpenChange, getAuthHeaders }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [dias, setDias] = useState(7);

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/alnap/share-links`, getAuthHeaders());
      const d = await r.json();
      setLinks(d.items || []);
    } catch {
      toast.error("Error al cargar enlaces");
    }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => {
    if (open) fetchLinks();
  }, [open, fetchLinks]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const r = await fetch(`${API_URL}/api/alnap/share-links`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ label: label || "Demo ALNAP", dias_vigencia: Number(dias) || 7 }),
      });
      if (!r.ok) throw new Error("fail");
      const newLink = await r.json();
      toast.success("Enlace generado");
      setLabel("");
      setDias(7);
      // Copiar automáticamente al portapapeles
      const url = `${window.location.origin}/alnap-public/${newLink.token}`;
      try { await navigator.clipboard.writeText(url); toast.success("URL copiada al portapapeles"); } catch {}
      fetchLinks();
    } catch {
      toast.error("No se pudo generar el enlace");
    }
    setCreating(false);
  };

  const handleCopy = async (token) => {
    const url = `${window.location.origin}/alnap-public/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copiada");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm("¿Revocar este enlace? El cliente ya no podrá acceder.")) return;
    try {
      const r = await fetch(`${API_URL}/api/alnap/share-links/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders().headers,
      });
      if (!r.ok) throw new Error("fail");
      toast.success("Enlace revocado");
      fetchLinks();
    } catch {
      toast.error("No se pudo revocar");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("es-DO", { dateStyle: "short", timeStyle: "short" }); }
    catch { return iso.slice(0, 16).replace("T", " "); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="share-links-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Share2 className="w-5 h-5 text-[#193966]" />
            Compartir Demo con ALNAP
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Generá un enlace público temporal (sin necesidad de credenciales) para enviarle al cliente. Cada acceso queda registrado.
          </p>
        </DialogHeader>

        {/* Crear nuevo */}
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardContent className="p-4 space-y-3">
            <p className="text-sm font-bold text-emerald-800 flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> Nuevo enlace público
            </p>
            <div className="grid md:grid-cols-3 gap-2">
              <div className="md:col-span-2">
                <Label htmlFor="link-label" className="text-xs">Etiqueta interna (opcional)</Label>
                <Input
                  id="link-label"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Ej: Reunión con ALNAP — Comité Operaciones"
                  className="h-9 text-sm"
                  data-testid="share-label-input"
                />
              </div>
              <div>
                <Label htmlFor="link-dias" className="text-xs">Vigencia (días)</Label>
                <Input
                  id="link-dias"
                  type="number"
                  min={1}
                  max={30}
                  value={dias}
                  onChange={(e) => setDias(e.target.value)}
                  className="h-9 text-sm"
                  data-testid="share-dias-input"
                />
              </div>
            </div>
            <Button
              onClick={handleCreate}
              disabled={creating}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
              data-testid="create-share-link-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
              Generar enlace y copiar URL
            </Button>
          </CardContent>
        </Card>

        {/* Lista */}
        <div className="space-y-2 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-slate-700">Enlaces existentes ({links.length})</p>
            {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          </div>

          {links.length === 0 && !loading && (
            <div className="text-center py-8 text-sm text-slate-400">
              <Link2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
              Aún no hay enlaces compartidos.
            </div>
          )}

          {links.map((link) => {
            const url = `${window.location.origin}/alnap-public/${link.token}`;
            const statusBadge = link.revoked
              ? <Badge variant="outline" className="bg-rose-100 text-rose-700 border-rose-300 text-[10px]">Revocado</Badge>
              : link.expired
              ? <Badge variant="outline" className="bg-slate-200 text-slate-600 border-slate-300 text-[10px]">Expirado</Badge>
              : <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px]">Activo</Badge>;
            return (
              <Card key={link.id} className="border-slate-200" data-testid={`share-link-row-${link.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 mb-1">
                        {statusBadge}
                        <span className="text-sm font-semibold text-slate-800">{link.label}</span>
                      </div>
                      <p className="text-[11px] font-mono text-slate-500 break-all bg-slate-100 px-2 py-1 rounded mb-1.5">
                        {url}
                      </p>
                      <div className="flex gap-3 text-[10px] text-slate-500">
                        <span>Expira: <b>{fmtDate(link.expires_at)}</b></span>
                        <span>Accesos: <b>{link.access_count || 0}</b></span>
                        {link.last_access_at && <span>Último: {fmtDate(link.last_access_at)}</span>}
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCopy(link.token)}
                        disabled={!link.active}
                        className="h-8 text-xs"
                        data-testid={`copy-link-${link.id}`}
                      >
                        <Copy className="w-3 h-3 mr-1" /> Copiar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(url, "_blank")}
                        disabled={!link.active}
                        className="h-8 text-xs text-blue-600 border-blue-300 hover:bg-blue-50"
                        data-testid={`open-link-${link.id}`}
                      >
                        <Eye className="w-3 h-3" />
                      </Button>
                      {!link.revoked && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRevoke(link.id)}
                          className="h-8 text-xs text-rose-600 border-rose-300 hover:bg-rose-50"
                          data-testid={`revoke-link-${link.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <DialogFooter className="text-[10px] text-slate-400 italic mt-2">
          Los enlaces vencen automáticamente y se revocan con un click. Cada acceso registra IP, fecha y dispositivo.
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
// === ALNAP END ===
